import { creditNoteStatusEnum } from "./credit-note";
import { estimateStatusEnum } from "./estimate";
import { invoiceStatusEnum } from "./invoice";
import { expenseStatusEnum } from "./expense";
import { z } from "zod";

// Report Option Enums
export const reportOptionEnum = z.enum([
  "activity",
  "sales",
  "sales-per-date",
  "sales-per-client",
  "sales-per-items",
  "payments",
  "payments-per-date",
  "payments-per-relation",
  "payments-per-type",
  "expenses",
  "expenses-per-date",
  "expenses-per-relation",
  "tax",
  "tax-per-designation",
]);
export type ReportOption = z.infer<typeof reportTypeEnum>;

// Report types enum
export const reportTypeEnum = z.enum(["invoice", "estimate", "credit-note", "expense", "payment"]);
export type ReportType = z.infer<typeof reportTypeEnum>;

// Period types enum
export const periodTypeEnum = z.enum(["this-month", "last-month", "this-year", "last-year", "custom"]);
export type PeriodType = z.infer<typeof periodTypeEnum>;

// Period types enum
export const displayByEnum = z.enum(["monthly", "quarterly", "yearly"]);
export type DisplayBy = z.infer<typeof displayByEnum>;

// Status filters for different report types - using actual status enums with "all" option
export const invoiceStatusFilterEnum = z.enum(["all", ...invoiceStatusEnum.options]);
export const estimateStatusFilterEnum = z.enum(["all", ...estimateStatusEnum.options]);
export const creditNoteStatusFilterEnum = z.enum(["all", ...creditNoteStatusEnum.options]);
export const expenseStatusFilterEnum = z.enum(["all", ...expenseStatusEnum.options]);
export const transactionTypeFilterEnum = z.enum(["all", "credit", "debit"]);

export type TransactionTypeFilter = z.infer<typeof transactionTypeFilterEnum>;

// Base reporting filters schema
export const reportingFiltersSchema = z
  .object({
    reportType: reportOptionEnum,
    period: periodTypeEnum.optional().nullable(),
    displayBy: displayByEnum.optional().nullable(),
    startDate: z.string().optional().nullable(), // Required when period is "custom"
    endDate: z.string().optional().nullable(), // Required when period is "custom"
    years: z.array(z.string()).optional().nullable(),
    invoiceStatus: z.array(invoiceStatusFilterEnum).optional().nullable(),
    estimateStatus: z.array(estimateStatusFilterEnum).optional().nullable(),
    creditNoteStatus: z.array(creditNoteStatusFilterEnum).optional().nullable(),
    expenseStatus: z.array(expenseStatusFilterEnum).optional().nullable(),
    transactionType: z.array(transactionTypeFilterEnum).optional().nullable(),
    relationId: z.string().optional().nullable(),
    projectId: z.string().optional().nullable(),
    taxId: z.string().optional().nullable(),
  })
  .refine(
    (data) => {
      // If period is custom, both startDate and endDate are required
      if (data.period === "custom") {
        return data.startDate && data.endDate;
      }
      return true;
    },
    {
      message: "Start date and end date are required when period is custom",
      path: ["startDate", "endDate"],
    },
  );

export type ReportingFiltersSchema = z.infer<typeof reportingFiltersSchema>;

// Report data item schema - generic structure for all report types
export const reportDocumentDataSchema = z.object({
  id: z.string(),
  number: z.string(), // invoice number, payment reference, etc.
  relationName: z.string(),
  total: z.number(),
  currency: z.string(),
  createdAt: z.string().datetime(),
  date: z.string().datetime(),
  status: z.string(),
  type: reportTypeEnum, // Helps identify what type of data this is
  // Additional metadata that might be useful
  metadata: z.record(z.any()).optional().nullable(),
});

export type ReportDocumentData = z.infer<typeof reportDocumentDataSchema>;

export const reportSalesDateDataSchema = z.object({
  name: z.string(),
  subtotal: z.number(),
  tax: z.number(),
  discount: z.number(),
  total: z.number(),
  currency: z.string(),
  paymentAmount: z.number(),
});

export type ReportSalesDateData = z.infer<typeof reportSalesDateDataSchema>;

export const reportSalesClientDataSchema = z.object({
  relationName: z.string(),
  subtotal: z.number(),
  tax: z.number(),
  discount: z.number(),
  total: z.number(),
  currency: z.string(),
  paymentAmount: z.number(),
});

export type ReportSalesClientData = z.infer<typeof reportSalesClientDataSchema>;

export const reportSalesItemDataSchema = z.object({
  id: z.string(),
  name: z.string(),
  unit: z.string(),
  quantity: z.number(),
  rate: z.number(),
  subTotal: z.number(),
  tax: z.number(),
  total: z.number(),
  currency: z.string(),
});

export type ReportSalesItemData = z.infer<typeof reportSalesItemDataSchema>;

export const reportPaymentDataSchema = z.object({
  name: z.string(),
  credit: z.number(),
  debit: z.number(),
  currency: z.string(),
});

export type ReportPaymentData = z.infer<typeof reportPaymentDataSchema>;

export const reportExpensesDateDataSchema = z.object({
  name: z.string(),
  subtotal: z.number(),
  tax: z.number(),
  discount: z.number(),
  total: z.number(),
  currency: z.string(),
  paymentAmount: z.number(),
});

export type ReportExpensesDateData = z.infer<typeof reportExpensesDateDataSchema>;

export const reportExpensesRelationDataSchema = z.object({
  relationName: z.string(),
  subtotal: z.number(),
  tax: z.number(),
  discount: z.number(),
  total: z.number(),
  currency: z.string(),
  paymentAmount: z.number(),
});

export type ReportExpensesRelationData = z.infer<typeof reportExpensesRelationDataSchema>;

export const reportTaxDataSchema = z.object({
  name: z.string(),
  salesVat: z.number(), // input tax (tax you pay)
  expensesVat: z.number(), // output tax (tax you collect)
  currency: z.string(),
});

export type ReportTaxData = z.infer<typeof reportTaxDataSchema>;
